import { AloftArea, AloftDateJSON, AloftPrice } from '@aloft/shared-objects';
import {
  AloftDate,
  ListingsFiltersMatchCriteriaKey,
  MainPropertyType,
  RecommendedCompsStatistics,
  SubjectData,
} from '@aloft/toolkit-shared';
import { Block, Button, Drawer, Flex, Icon, Modal, Typography } from '@aloft/toolkit-sui';

import { useState } from 'react';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import {
  BooleanSelect,
  HeaderRow,
  Row,
  StringSelect,
  getEnumOptions,
} from '../../../FilterDrawer/index.ts';
import { LoadingScreenSamInline } from '../../../LoadingScreen/LoadingScreenSamInline.tsx';
import { Table, Tbody, Td, Thead } from '../../../table.tsx';
import { useSubjectCraftsmanPropertyToCompSelectionProperty } from '../../hooks/useSubjectCraftsmanPropertyToCompSelectionProperty.ts';
import { FiltersMessage, RecommendCompsFormSchema } from '../../types.ts';
import { FilterDrawerMapView } from './FilterDrawerMapView.tsx';
import { FilterInputFields } from './FilterInputFields.tsx';
import { FilterStatistics } from './FilterStatistics.tsx';
import { filterHasValue } from '../../utils/filterHasValue.ts';

type Props = {
  subject: SubjectData;
  isOpen: boolean;
  effectiveDate: AloftDateJSON;
  isLoading: boolean;
  statistics: RecommendedCompsStatistics | undefined;
  filtersMessage?: FiltersMessage;
  subjectPropertyType?: MainPropertyType;
  allowApplyNewFilters: boolean;
  onCancel: () => void;
  onApply: () => void;
};

export const FilterDrawer = ({
  effectiveDate,
  isOpen,
  onCancel,
  onApply,
  subject,
  isLoading,
  statistics,
  filtersMessage,
  subjectPropertyType,
  allowApplyNewFilters,
}: Props) => {
  const [isCloseWarningModalOpen, setIsCloseWarningModalOpen] = useState(false);
  const { trigger, watch, setValue } = useZodFormContext<RecommendCompsFormSchema>();
  const effectiveDateAloft = AloftDate.fromIsoString(effectiveDate.isoString);
  const subjectComp = useSubjectCraftsmanPropertyToCompSelectionProperty(
    subject,
    effectiveDateAloft,
  );

  if (!isOpen) return null;

  const handleOnClose = () => {
    if (allowApplyNewFilters) {
      setIsCloseWarningModalOpen(true);
    } else {
      onCancel();
    }
  };

  const onSubmit = () => {
    const _ = trigger('search.filters').then((filterAreValid) => {
      if (filterAreValid) {
        onApply();
      }
    });
  };

  let propertyType;
  switch (subjectPropertyType) {
    case 'singleFamilyResidence':
      propertyType = 'SFR';
      break;
    case 'multiFamily':
      propertyType = 'Multi-Family';
      break;
    case 'manufacturedHome':
      propertyType = 'Manufactured';
      break;
    default:
      propertyType = '-';
      break;
  }

  const subjectSalePrice = subject.editableData?.transaction.price
    ? AloftPrice.fromUnitAmount(
      subject.editableData.transaction.price.unitAmount,
      subject.editableData.transaction.price.currency,
    ).toString()
    : '-';
  const subjectGLA = subject.editableData?.improvements.gla
    ? `${AloftArea.fromNumber(
      subject.editableData?.improvements.gla.unitAmount,
      subject.editableData?.improvements.gla.unit,
    ).toSqFtValueStringRounded()} sqft`
    : '-';
  // const subjectLotSize = subject.editableData?.site?.area
  //   ? `${AloftArea.fromNumber(
  //       subject.editableData?.site?.area.unitAmount,
  //       subject.editableData?.site?.area.unit,
  //     ).toSqFtValueStringRounded()} sqft`
  //   : '-';

  const filters = watch('search.filters');

  const clearFilter = (filterName: ListingsFiltersMatchCriteriaKey) => {
    setValue(`search.filters.${filterName}`, {
      max: '',
      min: '',
      exact: '',
      percentage: '',
      variance: '',
    });
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleOnClose}
      size="full"
      placement="bottom"
      header={<Typography variant="h1">Search Filters</Typography>}
      drawerContentProps={{
        backgroundColor: 'surface2',
        paddingX: '24px',
        paddingY: '22px',
      }}
      variant="overlay"
      closeOnOverlayClick={false}
      closeOnEsc={false}
    >
      <Modal
        isOpen={isCloseWarningModalOpen}
        onClose={onCancel}
        title='Unsaved Changes'
        primaryAction={{
          text: 'Apply & Close',
          onClick: () => {
            onApply();
            onCancel();
            setIsCloseWarningModalOpen(false);
          },
        }}
        secondaryAction={{
          text: 'Discard & Close',
          onClick: () => {
            onCancel();
            setIsCloseWarningModalOpen(false);
          },
        }}
        closeOnEsc={false}
        showCloseButton={false}
      >
        <Typography variant="body1">
          You&apos;ve updated the filters, but those changes haven&apos;t been applied yet. Do you want to apply them before closing?
        </Typography>
      </Modal>
      <Flex flexDirection="row" width="100%" height="100%" gap="24px">
        <Flex width="100%" height="100%" flexDirection="column" gap="24px">
          <Block>
            <Table>
              <Thead backgroundColor="surfaceAccent7">
                <HeaderRow includeMatchCriteria includeSubjectInfo />
              </Thead>
              <Tbody>
                {/* Shown to the user as property type, but this field internally represents property subtypes */}
                <Row label="Property Type">
                  <Td colSpan={1} />
                  <StringSelect
                    name="search.filters.propertySubType"
                    options={getEnumOptions({
                      name: 'search.filters.propertySubType',
                    })}
                    placeholder=" "
                  />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {propertyType}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                <Row label="Sale Date" includeClearButton={filterHasValue(filters.saleDate)} onClear={() => clearFilter('saleDate')}>
                  <StringSelect
                    name="search.matchCriteria.saleDate"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.saleDate',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields
                    name="saleDate"
                    type="date"
                    unitName="months (from Effective Date)"
                  />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject.editableData?.transaction.saleDate || '-'}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                <Row label="Sale Price" includeClearButton={filterHasValue(filters.salePrice)} onClear={() => clearFilter('salePrice')}>
                  <StringSelect
                    name="search.matchCriteria.salePrice"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.salePrice',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="salePrice" type="price" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subjectSalePrice}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                <Row label="GLA" includeClearButton={filterHasValue(filters.gla)} onClear={() => clearFilter('gla')}>
                  <StringSelect
                    name="search.matchCriteria.gla"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.gla',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="gla" type="area" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subjectGLA}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                {/* TODO: Add when supported by Abode */}
                {/* <Row label="Lot Size">
                  <StringSelect
                    name="search.matchCriteria.lotSize"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.lotSize',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="lotSize" type="area" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subjectLotSize}
                      </Typography>
                    </Block>
                  </Td>
                </Row> */}
                <Row label="Year Built" includeClearButton={filterHasValue(filters.yearBuilt)} onClear={() => clearFilter('yearBuilt')}>
                  <StringSelect
                    name="search.matchCriteria.yearBuilt"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.yearBuilt',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="yearBuilt" type="unit" unitName="years" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.readonlyData?.backupListingData?.listing?.YearBuilt?.toString() ||
                          '-'}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                <Row label="Bedrooms" includeClearButton={filterHasValue(filters.bedrooms)} onClear={() => clearFilter('bedrooms')}>
                  <StringSelect
                    name="search.matchCriteria.bedrooms"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.bedrooms',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="bedrooms" type="unit" unitName="bedrooms" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.readonlyData?.backupListingData?.listing?.BedroomsTotal?.toString() ||
                          '-'}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                <Row label="Baths: Full" includeClearButton={filterHasValue(filters.fullBaths)} onClear={() => clearFilter('fullBaths')}>
                  <StringSelect
                    name="search.matchCriteria.fullBaths"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.fullBaths',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="fullBaths" type="unit" unitName="baths" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.editableData?.improvements?.fullBaths?.toString() || '-'}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                <Row label="Stories" includeClearButton={filterHasValue(filters.stories)} onClear={() => clearFilter('stories')}>
                  <StringSelect
                    name="search.matchCriteria.stories"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.stories',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="stories" type="unit" unitName="stories" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.readonlyData?.backupListingData?.listing?.StoriesTotal?.toString() ||
                          '-'}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                {/* TODO: Add when supported by Abode */}
                {/* <Row label="Garage Spaces">
                  <StringSelect
                    name="search.matchCriteria.garageSpaces"
                    options={getEnumOptions({
                      name: 'search.matchCriteria.garageSpaces',
                    })}
                    colSpan={1}
                  />
                  <FilterInputFields name="garageSpaces" type="unit" unitName="garage spaces" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.editableData?.improvements?.garage?.spaces?.toString() || '-'}
                      </Typography>
                    </Block>
                  </Td>
                </Row> */}
                <Row label="Basement">
                  <Td colSpan={1} />
                  <BooleanSelect name="search.filters.hasBasement" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.readonlyData.backupListingData?.listing.BasementYN ? 'Yes' : 'No'}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
                <Row label="Pool">
                  <Td colSpan={1} />
                  <BooleanSelect name="search.filters.hasPool" />
                  <Td colSpan={1}>
                    <Block padding="0 5px">
                      <Typography variant="body1" color="textPrimary">
                        {subject?.readonlyData.backupListingData?.listing.PoolPrivateYN
                          ? 'Yes'
                          : 'No'}
                      </Typography>
                    </Block>
                  </Td>
                </Row>
              </Tbody>
            </Table>
          </Block>
          <Flex gap="8px" paddingTop="8px" marginY="20px">
            <Button variant="secondary" text="Close" onClick={handleOnClose} />
            <Button disabled={!allowApplyNewFilters || isLoading} text="Apply" onClick={onSubmit} />
            {isLoading && (
              <Block marginLeft="10px">
                <LoadingScreenSamInline
                  logoWidth="50px"
                  overrideText="Applying filters... this might take a few seconds"
                  overrideComponent={
                    <Typography color="textSecondary">
                      Applying filters... this might take a few seconds
                    </Typography>
                  }
                />
              </Block>
            )}
            {filtersMessage && (
              <Flex flexDirection="row" alignItems="center">
                <Flex
                  justifyContent="center"
                  alignItems="center"
                  width="24px"
                  height="24px"
                  borderRadius="50%"
                  borderColor={filtersMessage.color}
                  borderWidth="2px"
                  marginLeft="10px"
                  marginRight="10px"
                  borderStyle="solid"
                >
                  <Icon color={filtersMessage.color} icon={filtersMessage.icon} size="md" />
                </Flex>
                <Typography color="textSecondary">{filtersMessage.message}</Typography>
              </Flex>
            )}
          </Flex>
          <FilterStatistics statistics={statistics} isLoading={isLoading} />
        </Flex>

        <FilterDrawerMapView subjectComp={subjectComp} />
      </Flex>
    </Drawer>
  );
};
